import React, { useEffect } from 'react';
import { usePostHog } from 'posthog-js/react';
import toast from 'react-hot-toast';
import './Pricing.css';
import { PricingTable } from '@clerk/clerk-react';

const Pricing: React.FC = () => {
  const posthog = usePostHog();

  // Track pricing page view
  useEffect(() => {
    posthog?.capture('pricing_page_viewed');
  }, [posthog]);

  // Track subscription button clicks via DOM event delegation
  useEffect(() => {
    const handleSubscribeClick = (event: Event) => {
      const target = event.target as HTMLElement;
      
      // Look for Clerk subscription buttons using the actual CSS classes
      if (
        target.classList.contains('cl-pricingTableCardFooterButton') ||
        target.closest('.cl-pricingTableCardFooterButton') ||
        (target.tagName === 'BUTTON' && target.closest('.cl-pricingTableCardFooter'))
      ) {
        // Find the pricing card container
        const pricingCard = target.closest('.cl-pricingTableCard') || 
                           target.closest('[class*="cl-pricingTableCard"]');
        
        let planName = 'Unknown';
        let price = 'Unknown';
        let planType = 'Unknown';
        
        if (pricingCard) {
          // Extract plan name from the title
          const titleElement = pricingCard.querySelector('.cl-pricingTableCardTitle');
          if (titleElement) {
            planName = titleElement.textContent?.trim() || 'Unknown';
            
            // Extract plan type (2k, 10k, 20k) from plan name
            const planMatch = planName.toLowerCase().match(/(\d+k?)\s*credits?/);
            if (planMatch) {
              planType = planMatch[1];
            }
          }
          
          // Extract price from the fee element
          const priceElement = pricingCard.querySelector('.cl-pricingTableCardFee');
          if (priceElement) {
            price = priceElement.textContent?.trim() || 'Unknown';
          }
          
          // Get button text to distinguish between Subscribe, Resubscribe, Switch Plan
          const buttonText = target.textContent?.trim() || 'Unknown';
          
          // Track the subscription button click
          posthog?.capture('subscribe_button_clicked', {
            plan_type: planType, // '2k', '10k', or '20k'
            plan_name: planName,
            price: price,
            button_text: buttonText,
            page: 'pricing',
            action_type: buttonText.toLowerCase().includes('resubscribe') ? 'resubscribe' : 
                        buttonText.toLowerCase().includes('switch') ? 'switch_plan' : 'subscribe',
            timestamp: new Date().toISOString()
          });
          
        }
      }
    };

    // Add event listener to the document to catch all clicks
    document.addEventListener('click', handleSubscribeClick, true);
    
    // Cleanup
    return () => {
      document.removeEventListener('click', handleSubscribeClick, true);
    };
  }, [posthog]);

  // Also listen for successful subscription events via Clerk's events
  useEffect(() => {
    const handleClerkEvent = (event: CustomEvent) => {
      if (event.type === 'clerk:subscription:created' || 
          event.type === 'clerk:billing:checkout:completed') {
        const { subscription, plan } = event.detail || {};
        
        posthog?.capture('subscription_completed', {
          plan_id: plan?.id || subscription?.planId,
          plan_name: plan?.name || subscription?.planName,
          price: plan?.price || subscription?.price,
          page: 'pricing',
          subscription_id: subscription?.id,
          timestamp: new Date().toISOString()
        });
        
        toast.success('Subscription successful!');
      }
    };

    // Listen for Clerk billing events
    window.addEventListener('clerk:subscription:created', handleClerkEvent as EventListener);
    window.addEventListener('clerk:billing:checkout:completed', handleClerkEvent as EventListener);
    
    return () => {
      window.removeEventListener('clerk:subscription:created', handleClerkEvent as EventListener);
      window.removeEventListener('clerk:billing:checkout:completed', handleClerkEvent as EventListener);
    };
  }, [posthog]);

  return (
    <div className="pricing-container">
      <div className="pricing-header">
        <h1>Choose Your Plan</h1>
        <p>Select the perfect plan for your spreadsheet needs</p>
      </div>
      <div className="pricing-cards">
        <PricingTable />
      </div>
    </div>
  );
};

export default Pricing; 